/**
 * Integrations configuration guide (AUTONOMY · Layer 3).
 */
import { msg } from "@lingui/core/macro";
import { Plug } from "@fleet/icons";

import type { HelpGuide } from "../types";
import { Bullets, GoTo, Note, Steps } from "../components/help-prose";

export const integrationsGuide: HelpGuide = {
  key: "help.integrations",
  slug: "integraciones",
  title: msg`Configuring integrations`,
  description: msg`Connect email (SMTP), Stripe payments, the BCV exchange rate and AI assistance — each with its own form and test button.`,
  audience: msg`Platform administrators`,
  icon: Plug,
  sections: [
    {
      id: "panorama",
      title: msg`How integrations work`,
      summary: msg`Each integration has a form generated from its own schema, with a scope selector: a global platform default plus overrides per academy.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Secrets (passwords, API keys) are sealed server-side and never shown again — a saved secret displays only as "set".`,
              msg`Values resolve in layers: environment defaults, then the global row, then the academy override. Leaving a field blank keeps the inherited value.`,
              msg`Every integration has a Test button that checks the real connection and records the result.`,
            ]}
          />
          <GoTo to="/admin/integrations" label={msg`Open Integrations`} />
        </>
      ),
    },
    {
      id: "smtp",
      title: msg`Email (SMTP)`,
      summary: msg`SMTP delivers all platform email: invitations, verification, campaigns. Each academy can use its own SMTP server or inherit the global one.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Choose the scope: global default or a specific academy.`,
              msg`Fill host, port, from address and credentials.`,
              msg`Use Test connection before saving to verify the server accepts the credentials.`,
            ]}
          />
          <GoTo to="/admin/integrations" label={msg`Configure SMTP`} />
        </>
      ),
    },
    {
      id: "stripe",
      title: msg`Stripe (online payments)`,
      summary: msg`Stripe enables online card checkout for tuition. Configure the secret key and webhook secret; verified payments arrive via webhook.`,
      body: () => (
        <>
          <Note
            msg={msg`After configuring Stripe, enable the Stripe payment method in Billing configuration so it appears at checkout.`}
          />
          <GoTo to="/admin/integrations" label={msg`Configure Stripe`} />
        </>
      ),
    },
    {
      id: "tasa-bcv",
      title: msg`BCV exchange rate`,
      summary: msg`The BCV integration fetches the official VES/USD rate on demand from the Rates screen. You can override the source URL if the official one changes.`,
      body: () => (
        <>
          <GoTo to="/finance/rates" label={msg`Open Rates`} />
        </>
      ),
    },
    {
      id: "ai",
      title: msg`AI assistance`,
      summary: msg`The AI integration powers assisted drafting (e.g. email templates). Configure the API key and model; without it, AI features stay hidden.`,
      body: () => (
        <>
          <GoTo to="/admin/integrations" label={msg`Configure AI`} />
        </>
      ),
    },
  ],
};
